import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { invoiceService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../utils/permissions';
import {
  FileText,
  Plus,
  Send,
  Download,
  DollarSign,
  Calendar,
  User,
  Building,
  AlertTriangle,
  CheckCircle,
  Clock,
  Ban,
  Mail,
  CreditCard,
  Trash2,
  ShieldCheck,
  RotateCcw,
  Landmark,
  ChevronDown,
  Briefcase
} from 'lucide-react';
import AddPaymentModal from '../../components/invoices/AddPaymentModal';
import SendInvoiceModal from '../../components/invoices/SendInvoiceModal';
import CollectPaymentModal from '../../components/invoices/CollectPaymentModal';
import CreatePaymentPlanModal from '../../components/invoices/CreatePaymentPlanModal';
import EditableLineItems from '../../components/invoices/EditableLineItems';
import lawpayService from '../../services/lawpayService';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';
import Breadcrumb from '../../components/common/Breadcrumb';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = hasPermission(user, 'approve_invoice');
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCollectPayment, setShowCollectPayment] = useState(false);
  const [showPaymentPlanModal, setShowPaymentPlanModal] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  const fetchInvoiceDetails = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await invoiceService.getInvoice(invoiceId);
      setInvoice(response.data);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  const fetchPaymentPlan = React.useCallback(async () => {
    try {
      const response = await lawpayService.getPaymentPlans({ invoice_id: invoiceId });
      const plans = response.data || [];
      const activePlan = plans.find(p => ['active', 'paused', 'draft'].includes(p.status));
      if (activePlan) {
        const detailRes = await lawpayService.getPaymentPlan(activePlan.id);
        setPaymentPlan(detailRes.data);
      } else {
        setPaymentPlan(null);
      }
    } catch {
      // Payment plans may not be set up yet — that's fine
      setPaymentPlan(null);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails();
      fetchPaymentPlan();
    }
  }, [invoiceId, fetchInvoiceDetails, fetchPaymentPlan]);

  const handleSendInvoice = async (email) => {
    try {
      await invoiceService.sendInvoice(invoiceId, email);
      fetchInvoiceDetails(); // Refresh to show updated status
      setShowSendModal(false);
    } catch (error) {
      console.error('Error sending invoice:', error);
      throw error; // Re-throw so SendInvoiceModal can handle it
    }
  };

  const handleVoidInvoice = () => {
    setShowVoidConfirm(true);
  };

  const confirmVoidInvoice = async () => {
    try {
      await invoiceService.voidInvoice(invoiceId);
      await fetchInvoiceDetails(); // Refresh to show updated status
      showSuccess('Invoice voided successfully');
    } catch (error) {
      console.error('Error voiding invoice:', error);
      showError('Failed to void invoice');
    } finally {
      setShowVoidConfirm(false);
    }
  };

  const confirmDeleteInvoice = async () => {
    try {
      await invoiceService.deleteInvoice(invoiceId);
      navigate('/invoices');
      showSuccess('Invoice deleted successfully');
    } catch (error) {
      console.error('Error deleting invoice:', error);
      showError('Failed to delete invoice');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      await invoiceService.submitForApproval(invoiceId);
      await fetchInvoiceDetails();
      showSuccess('Invoice submitted for approval');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to submit for approval');
    }
  };

  const handleApproveInvoice = async () => {
    try {
      await invoiceService.approveInvoice(invoiceId);
      await fetchInvoiceDetails();
      showSuccess('Invoice approved');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to approve invoice');
    }
  };

  const handleRejectInvoice = async () => {
    try {
      await invoiceService.rejectInvoice(invoiceId);
      await fetchInvoiceDetails();
      showSuccess('Invoice returned to draft');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to reject invoice');
    }
  };

  const handleApplyTrust = async () => {
    try {
      await invoiceService.applyTrust(invoiceId);
      await fetchInvoiceDetails();
      showSuccess('Trust funds applied');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to apply trust funds');
    }
  };

  const handleApproveAndApplyTrust = async () => {
    try {
      await invoiceService.approveInvoice(invoiceId);
      try {
        await invoiceService.applyTrust(invoiceId);
        showSuccess('Invoice approved and trust funds applied');
      } catch (trustError) {
        const msg = trustError.response?.data?.detail || 'no trust available';
        showSuccess(`Invoice approved (${msg})`);
      }
      await fetchInvoiceDetails();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to approve invoice');
    }
  };

  const handlePaymentAdded = () => {
    fetchInvoiceDetails(); // Refresh to show updated payment info
    setShowPaymentModal(false);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await invoiceService.downloadInvoicePDF(invoiceId);
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename with case title
      const caseTitle = invoice.case_title ? `_${invoice.case_title.replace(/[^a-zA-Z0-9 _-]/g, '').trim()}` : '';
      const filename = `Invoice_${invoice.invoice_number || invoiceId}${caseTitle}.pdf`;
      link.setAttribute('download', filename);
      
      // Append to body and click
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // You could add a toast notification here
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending_approval': return <Clock className="w-5 h-5 text-orange-600" />;
      case 'approved': return <ShieldCheck className="w-5 h-5 text-blue-600" />;
      case 'sent': return <Send className="w-5 h-5 text-yellow-600" />;
      case 'partial': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'overdue': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'void': return <Ban className="w-5 h-5 text-gray-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Invoice not found</h2>
        <p className="text-gray-600 mb-6">The invoice you're looking for doesn't exist or has been removed.</p>
        <Link
          to="/invoices"
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
        >
          Back to Invoices
        </Link>
      </div>
    );
  }

  const breadcrumbItems = [
    ...(invoice.client_id
      ? [{ label: invoice.client_name || 'Client', icon: User, to: `/clients/${invoice.client_id}` }]
      : []),
    ...(invoice.case_id
      ? [{ label: `${invoice.case_number || ''} - ${invoice.case_title || 'Case'}`.replace(/^ - /, ''), icon: Briefcase, to: `/cases/${invoice.case_id}` }]
      : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <Breadcrumb items={breadcrumbItems} />
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <FileText className="w-8 h-8 mr-3 text-yellow-600" />
                  {invoice.invoice_number}
                </h1>
                <p className="text-gray-600">
                  Invoice for {invoice.client_name} • {invoice.case_title}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${invoiceService.getStatusColor(invoice.status)}`}>
                {getStatusIcon(invoice.status)}
                <span className="ml-2">{invoiceService.getStatusLabel(invoice.status)}</span>
              </div>
              
              {/* Actions Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  onBlur={() => setTimeout(() => setShowActionsMenu(false), 200)}
                  className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center"
                >
                  Actions
                  <ChevronDown className="w-4 h-4 ml-2" />
                </button>
                {showActionsMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {/* Always available */}
                    <button
                      onClick={() => { handleDownloadPDF(); setShowActionsMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-3 text-gray-400" />
                      Download PDF
                    </button>

                    {/* Draft actions */}
                    {invoice.status === 'draft' && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => { handleSubmitForApproval(); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Send className="w-4 h-4 mr-3 text-yellow-500" />
                          Send for Approval
                        </button>
                        {canApprove && (
                          <>
                            <button
                              onClick={() => { handleApproveInvoice(); setShowActionsMenu(false); }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            >
                              <ShieldCheck className="w-4 h-4 mr-3 text-green-500" />
                              Approve
                            </button>
                            <button
                              onClick={() => { handleApproveAndApplyTrust(); setShowActionsMenu(false); }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            >
                              <Landmark className="w-4 h-4 mr-3 text-blue-500" />
                              Approve and Apply Trust
                            </button>
                          </>
                        )}
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => { handleVoidInvoice(); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        >
                          <Ban className="w-4 h-4 mr-3" />
                          Void
                        </button>
                        <button
                          onClick={() => { setShowDeleteConfirm(true); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-3" />
                          Delete
                        </button>
                      </>
                    )}

                    {/* Pending approval actions */}
                    {invoice.status === 'pending_approval' && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        {canApprove && (
                          <>
                            <button
                              onClick={() => { handleApproveInvoice(); setShowActionsMenu(false); }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            >
                              <ShieldCheck className="w-4 h-4 mr-3 text-green-500" />
                              Approve
                            </button>
                            <button
                              onClick={() => { handleApproveAndApplyTrust(); setShowActionsMenu(false); }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            >
                              <Landmark className="w-4 h-4 mr-3 text-blue-500" />
                              Approve and Apply Trust
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => { handleRejectInvoice(); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <RotateCcw className="w-4 h-4 mr-3 text-orange-500" />
                          Return to Draft
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => { handleVoidInvoice(); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        >
                          <Ban className="w-4 h-4 mr-3" />
                          Void
                        </button>
                      </>
                    )}

                    {/* Approved actions */}
                    {invoice.status === 'approved' && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => { handleApplyTrust(); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Landmark className="w-4 h-4 mr-3 text-blue-500" />
                          Apply Trust
                        </button>
                        <button
                          onClick={() => { setShowSendModal(true); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Send className="w-4 h-4 mr-3 text-yellow-500" />
                          Send Invoice
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => { handleVoidInvoice(); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        >
                          <Ban className="w-4 h-4 mr-3" />
                          Void
                        </button>
                      </>
                    )}

                    {/* Sent/Partial actions */}
                    {(invoice.status === 'sent' || invoice.status === 'partial') && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => { setShowSendModal(true); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Send className="w-4 h-4 mr-3 text-yellow-500" />
                          Resend Invoice
                        </button>
                        <button
                          onClick={() => { setShowPaymentModal(true); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <DollarSign className="w-4 h-4 mr-3 text-green-500" />
                          Record Payment
                        </button>
                        <button
                          onClick={() => { setShowCollectPayment(true); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <CreditCard className="w-4 h-4 mr-3 text-blue-500" />
                          Collect Payment
                        </button>
                        {!paymentPlan && (
                          <button
                            onClick={() => { setShowPaymentPlanModal(true); setShowActionsMenu(false); }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <Calendar className="w-4 h-4 mr-3 text-yellow-500" />
                            Payment Plan
                          </button>
                        )}
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => { handleVoidInvoice(); setShowActionsMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        >
                          <Ban className="w-4 h-4 mr-3" />
                          Void
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('line-items')}
              className={`py-3 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'line-items'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Line Items
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`py-3 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'payments'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Payments ({invoice.payments?.length || 0})
            </button>
            {paymentPlan && (
              <button
                onClick={() => setActiveTab('payment-plan')}
                className={`py-3 px-4 border-b-2 font-medium text-sm ${
                  activeTab === 'payment-plan'
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Payment Plan
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Financial Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">{invoiceService.formatCurrency(invoice.subtotal)}</span>
                  </div>
                  {parseFloat(invoice.discount_amount || 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-orange-600">Discount:</span>
                      <span className="font-medium text-orange-600">-{invoiceService.formatCurrency(invoice.discount_amount)}</span>
                    </div>
                  )}
                  {parseFloat(invoice.tax_amount) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Tax ({invoice.tax_rate}%):</span>
                      <span className="font-medium">{invoiceService.formatCurrency(invoice.tax_amount)}</span>
                    </div>
                  )}
                  {parseFloat(invoice.late_fee_amount) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-red-600">Late Fee:</span>
                      <span className="font-medium text-red-600">{invoiceService.formatCurrency(invoice.late_fee_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-lg font-bold border-t pt-4">
                    <span>Total Amount:</span>
                    <span>{invoiceService.formatCurrency(invoice.total_amount)}</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Amount Paid:</span>
                    <span className="font-medium text-green-600">{invoiceService.formatCurrency(invoice.paid_amount)}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Balance Due:</span>
                    <span className={invoiceService.calculateBalance(invoice) > 0 ? 'text-red-600' : 'text-green-600'}>
                      {invoiceService.formatCurrency(invoiceService.calculateBalance(invoice))}
                    </span>
                  </div>
                  
                  {invoice.is_overdue && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center text-red-800">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        <span className="text-sm font-medium">
                          Overdue by {invoiceService.getDaysOverdue(invoice)} days
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {invoice.description && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{invoice.description}</p>
              </div>
            )}

            {/* Client Notes */}
            {invoice.client_notes && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Client Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{invoice.client_notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Invoice Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Details</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Invoice Date</p>
                    <p className="font-medium">{invoiceService.formatDate(invoice.invoice_date)}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Due Date</p>
                    <p className={`font-medium ${invoice.is_overdue ? 'text-red-600' : ''}`}>
                      {invoiceService.formatDate(invoice.due_date)}
                    </p>
                  </div>
                </div>
                
                {invoice.sent_date && (
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Sent Date</p>
                      <p className="font-medium">{invoiceService.formatDate(invoice.sent_date)}</p>
                      {invoice.sent_to_email && (
                        <p className="text-xs text-gray-500">to {invoice.sent_to_email}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {invoice.paid_date && (
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Paid Date</p>
                      <p className="font-medium">{invoiceService.formatDate(invoice.paid_date)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Case & Client Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Case & Client</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Building className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Case</p>
                    <Link 
                      to={`/cases/${invoice.case_id}`}
                      className="font-medium text-yellow-600 hover:text-yellow-600"
                    >
                      {invoice.case_number} - {invoice.case_title}
                    </Link>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <User className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Client</p>
                    <p className="font-medium">{invoice.client_name}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <User className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Billing Attorney</p>
                    <p className="font-medium">{invoice.billing_attorney_name}</p>
                  </div>
                </div>

                {invoice.created_by_name && (
                  <div className="flex items-center">
                    <User className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Created by</p>
                      <p className="font-medium">{invoice.created_by_name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'line-items' && (
        <EditableLineItems
          invoice={invoice}
          onInvoiceUpdated={fetchInvoiceDetails}
          showNotification={(variant, message) => variant === 'success' ? showSuccess(message) : showError(message)}
        />
      )}

      {activeTab === 'payments' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Payment History</h3>
            {(invoice.status === 'sent' || invoice.status === 'partial') && (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Payment
              </button>
            )}
          </div>
          
          {invoice.payments && invoice.payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recorded By
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoice.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {invoiceService.formatDate(payment.payment_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {payment.payment_method || 'Not specified'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {payment.reference_number || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-green-600 text-right">
                        {invoiceService.formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {payment.recorded_by_name || 'Unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No payments recorded yet</p>
              {(invoice.status === 'sent' || invoice.status === 'partial') && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="text-yellow-600 hover:text-yellow-600"
                >
                  Record the first payment
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'payment-plan' && paymentPlan && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{paymentPlan.plan_name || 'Payment Plan'}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {paymentPlan.installments_completed} of {paymentPlan.number_of_installments} installments completed
                {paymentPlan.auto_charge && ' · Auto-charge enabled'}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              paymentPlan.status === 'active' ? 'bg-green-100 text-green-800' :
              paymentPlan.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
              paymentPlan.status === 'completed' ? 'bg-blue-100 text-blue-800' :
              paymentPlan.status === 'draft' ? 'bg-gray-100 text-gray-800' :
              'bg-red-100 text-red-800'
            }`}>
              {paymentPlan.status.charAt(0).toUpperCase() + paymentPlan.status.slice(1)}
            </span>
          </div>

          {/* Progress bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Progress</span>
              <span className="font-medium text-gray-900">
                {invoiceService.formatCurrency(paymentPlan.total_paid)} of {invoiceService.formatCurrency(paymentPlan.total_amount)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-green-500 h-2.5 rounded-full transition-all"
                style={{ width: `${Math.min((paymentPlan.total_paid / paymentPlan.total_amount) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Installments table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Processed</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(paymentPlan.installments || []).map((inst) => (
                  <tr key={inst.id}>
                    <td className="px-6 py-3 text-sm text-gray-900">{inst.installment_number}</td>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      {inst.due_date ? new Date(inst.due_date + 'T00:00:00').toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                      {invoiceService.formatCurrency(inst.amount)}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        inst.status === 'completed' ? 'bg-green-100 text-green-800' :
                        inst.status === 'pending' || inst.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        inst.status === 'failed' ? 'bg-red-100 text-red-800' :
                        inst.status === 'cancelled' || inst.status === 'skipped' ? 'bg-gray-100 text-gray-600' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {inst.status.charAt(0).toUpperCase() + inst.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">
                      {inst.processed_at ? new Date(inst.processed_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showPaymentModal && (
        <AddPaymentModal
          invoice={invoice}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentAdded}
        />
      )}

      {showSendModal && (
        <SendInvoiceModal
          invoice={invoice}
          onClose={() => setShowSendModal(false)}
          onSuccess={handleSendInvoice}
        />
      )}

      {showCollectPayment && (
        <CollectPaymentModal
          invoice={invoice}
          onClose={() => setShowCollectPayment(false)}
          onSuccess={() => {
            setShowCollectPayment(false);
            fetchInvoiceDetails();
            showSuccess('Payment collected successfully');
          }}
        />
      )}

      {showPaymentPlanModal && (
        <CreatePaymentPlanModal
          invoice={invoice}
          onClose={() => setShowPaymentPlanModal(false)}
          onSuccess={() => {
            setShowPaymentPlanModal(false);
            fetchPaymentPlan();
            showSuccess('Payment plan created successfully');
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showVoidConfirm}
        onClose={() => setShowVoidConfirm(false)}
        onConfirm={confirmVoidInvoice}
        title="Void Invoice"
        message="Are you sure you want to void this invoice? This action cannot be undone."
        confirmText="Void Invoice"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteInvoice}
        title="Delete Invoice"
        message="Are you sure you want to delete this draft invoice? This action cannot be undone."
        confirmText="Delete Invoice"
        cancelText="Cancel"
        variant="danger"
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default InvoiceDetail;